import { ServiceSettingDashboardPageStore } from './service-setting-dashboard-page.store';
import { fb, routeParams } from '../../../../modules/shared/shared.global-service';
import { Component, computed, effect, inject, viewChild } from '@angular/core';
import {
    SERVICE_SETTINGS_DASHBOARD_ANAMNESIS_FIELDS_DEFAULT,
    SERVICE_SETTINGS_DASHBOARD_TYPE_KEY,
} from '@libs/types/src/services/types/common-service-settings.type';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { isFolderStatusAllowed } from '@libs/types/src/services/enum/folder-status.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { contentGlobalSuccess } from '../../../../modules/content/content.error';
import {
    ServiceSettingsCommonComponent,
} from '../../../../modules/content/components/services/settings/service-settings-common/service-settings-common.component';
import { Divider } from '@openng/optimus-ui/divider';
import { CheckInputComponent } from '../../../kit/input/check-input/check-input.component';
import { ActionButtonsComponent } from '../../../kit/action-buttons/action-buttons.component';
import { featureOn } from '../../../../feature-flag';

export const FOLDER_DASHBOARD_WIDGET_STATE_HISTORY = 'stateHistory';
export const FOLDER_DASHBOARD_WIDGET_ERRORS = 'errors';
export const FOLDER_DASHBOARD_WIDGET_IMPORTANT_DATA = 'importantData';
export const FOLDER_DASHBOARD_WIDGET_LAST_CONTINUOUS_RECORD = 'lastContinuousRecord';
export const FOLDER_DASHBOARD_WIDGET_NOTE = 'note';
export const FOLDER_DASHBOARD_WIDGET_RISKS = 'risks';
export const FOLDER_DASHBOARD_WIDGET_EPICRISIS = 'epicrisis';
export const FOLDER_DASHBOARD_WIDGET_SERVICE_RECORD = 'serviceRecord';
export const FOLDER_DASHBOARD_WIDGET_MEDICATION = 'medication';
export const FOLDER_DASHBOARD_WIDGET_BILLING_STATUS = 'billingStatus';
export const FOLDER_DASHBOARD_WIDGET_PROTOCOLS = 'protocols';
export const FOLDER_DASHBOARD_WIDGET_PLANS = 'plans';
export const FOLDER_DASHBOARD_WIDGET_ANAMNESIS = 'anamnesis';

export const FOLDER_DASHBOARD_ANAMNESIS_FIELDS = Object.keys(
    SERVICE_SETTINGS_DASHBOARD_ANAMNESIS_FIELDS_DEFAULT,
);

export const healthCareWidgets = [
    FOLDER_DASHBOARD_WIDGET_MEDICATION,
    FOLDER_DASHBOARD_WIDGET_IMPORTANT_DATA,
    FOLDER_DASHBOARD_WIDGET_EPICRISIS,
    FOLDER_DASHBOARD_WIDGET_SERVICE_RECORD,
];

export const socialWidgets = [
    ...(featureOn.SOCIAL_FOLDER_DASHBOARD ? [FOLDER_DASHBOARD_WIDGET_BILLING_STATUS, FOLDER_DASHBOARD_WIDGET_PROTOCOLS, FOLDER_DASHBOARD_WIDGET_PLANS, FOLDER_DASHBOARD_WIDGET_LAST_CONTINUOUS_RECORD] : [FOLDER_DASHBOARD_WIDGET_LAST_CONTINUOUS_RECORD]),
];

@Component({
    selector: 'app-service-setting-dashboard-page',
    templateUrl: './service-setting-dashboard-page.component.html',
    styleUrl: './service-setting-dashboard-page.component.scss',
    providers: [ServiceSettingDashboardPageStore],
    imports: [
        ServiceSettingsCommonComponent,
        Divider,
        CheckInputComponent,
        ActionButtonsComponent,
    ],
})
export class ServiceSettingDashboardPageComponent {

    protected readonly store = inject(ServiceSettingDashboardPageStore);
    protected readonly route = inject(ActivatedRoute);
    protected readonly router = inject(Router);

    protected serviceSettingsCommonComponent = viewChild(ServiceSettingsCommonComponent);

    protected form = fb.group({
        stateHistory: fb.control(true),
        medication: fb.control(true),
        errors: fb.control(true),
        importantData: fb.control(true),
        lastContinuousRecord: fb.control(true),
        note: fb.control(true),
        risks: fb.control(true),
        epicrisis: fb.control(true),
        serviceRecord: fb.control(true),
        billingStatus: fb.control(true),
        protocols: fb.control(true),
        plans: fb.control(true),
        anamnesis: fb.control(true),
        anamnesisFields: fb.group({
            anamnesis: fb.control(true),
            socialAnamnesis: fb.control(true),
            nurseRecommendations: fb.control(true),
            statusPraesens: fb.control(true),
        }),
    });

    protected readonly anamnesisWidgetKey = FOLDER_DASHBOARD_WIDGET_ANAMNESIS;
    protected readonly anamnesisFields = FOLDER_DASHBOARD_ANAMNESIS_FIELDS;

    protected allowedWidgets = computed(() => {
        const type = this.store.service()?.type;
        const isHealthCare = type === ServiceTypeEnum.HEALTH_CARE;
        const isSocial = type === ServiceTypeEnum.SOCIAL;
        const expertiseCode = this.store.service()?.expertise?.code;
        const anamnesisAllowed = isFolderStatusAllowed(expertiseCode).anamnesisAllowed;

        return Object.keys(this.form.controls)
            .filter((key) => key !== 'anamnesisFields')
            .filter((it) => {
                if (it === FOLDER_DASHBOARD_WIDGET_ANAMNESIS) {
                    return isHealthCare && anamnesisAllowed;
                }
                if (healthCareWidgets.includes(it)) {
                    return isHealthCare;
                }
                if (socialWidgets.includes(it)) {
                    return isSocial;
                }

                return true;
            })
            ;
    });

    constructor() {
        effect(() => {
            const setting = this.store.service()?.serviceSettings?.find((it) => it.key === SERVICE_SETTINGS_DASHBOARD_TYPE_KEY);
            if (!setting) return;

            const data = JSON.parse(setting.data);
            this.form.patchValue(data);
            this.serviceSettingsCommonComponent()?.form.patchValue(data);
        });
    }

    protected async save(): Promise<void> {
        const serviceId = routeParams.serviceId();
        if (!serviceId) return;

        const data = {
            ...JSON.parse(this.serviceSettingsCommonComponent()?.getFormData() || '{}'),
            ...this.form.value,
        };

        await this.store.api.updateServiceSettings({
            serviceId: Number(serviceId),
            key: SERVICE_SETTINGS_DASHBOARD_TYPE_KEY,
            data: JSON.stringify(data),
        });
        contentGlobalSuccess.next('common.updated');
    }

    protected async cancel(): Promise<void> {
        await this.router.navigate(['../../basic-information'], { relativeTo: this.route });
    }

}
